import { FC, HTMLAttributes, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { Flex, Table, TableColumnsType } from 'antd';

import { adspaceApi, EAdspaceStatus, getAdspace, useGetAdspaceListQuery, useGetAdspaceQuery } from 'entities/Adspace';
import { Pagination } from 'features/Pagination';
import { EApiTags } from 'shared/typings';
import { EmptyState, OverlayLoader } from 'shared/ui';
import { getDefaultSortOrder, getTableSorter } from 'shared/utils';

interface DataType {
	key: React.Key;
	name: ReactNode;
	status: ReactNode;
	buttons: ReactNode;
}

interface IAdspaceListProps extends HTMLAttributes<HTMLDivElement> {}

export const AdspaceList: FC<IAdspaceListProps> = ({ className, ...props }) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const [searchParams, setSearchParams] = useSearchParams();
	const creatingAdspaceId = searchParams.get('adspaceId') || '';
	const [creatingAdspaceIsReady, setCreatingAdspaceIsReady] = useState(false);
	const { data, isLoading } = useGetAdspaceListQuery(searchParams.toString(), {
		pollingInterval: creatingAdspaceId ? 1000 : undefined,
	});
	const { data: creatingAdspace } = useGetAdspaceQuery(creatingAdspaceId, {
		skip: !creatingAdspaceId || creatingAdspaceIsReady,
		pollingInterval: 1000,
	});

	useEffect(() => {
		if (creatingAdspace?.adspace_id) {
			setCreatingAdspaceIsReady(true);
			dispatch(adspaceApi.util.invalidateTags([EApiTags.ADSPACES_LIST]));
		}
	}, [creatingAdspace?.adspace_id, dispatch]);

	const columns: TableColumnsType<DataType> = [
		{
			dataIndex: 'name',
			key: 'name',
			title: `${t('Площадка')}`,
			defaultSortOrder: getDefaultSortOrder('name', searchParams),
			sorter: true,
		},
		{
			dataIndex: 'status',
			key: 'status',
			title: t('Статус'),
			defaultSortOrder: getDefaultSortOrder('status', searchParams),
			sorter: true,
		},
		{
			dataIndex: 'buttons',
			key: 'buttons',
			width: 184,
		},
	];

	const dataSource =
		data?.results?.map<DataType>((adspace, i) => {
			return getAdspace(adspace, i);
		}) || [];

	if (dataSource && creatingAdspaceId && !creatingAdspaceIsReady && !isLoading) {
		dataSource.unshift(
			getAdspace(
				{
					adspace_id: creatingAdspaceId,
					status: EAdspaceStatus.CREATING,
					name: t('Новая площадка'),
				},
				-1,
			),
		);
	}

	return (
		<Flex vertical gap={20} {...props} className={className}>
			{isLoading && <OverlayLoader />}

			{!dataSource?.length && !isLoading && (
				<EmptyState title={t('Нет платформ')} desc={t('После создания платформы она появится здесь.')} />
			)}

			{!!dataSource?.length && (
				<>
					<Table<DataType>
						columns={columns}
						dataSource={dataSource}
						bordered={false}
						scroll={{ x: 'max-content' }}
						pagination={false}
						showSorterTooltip={false}
						onChange={getTableSorter<DataType>(searchParams, setSearchParams)}
					/>

					<Pagination count={data?.count || 1} />
				</>
			)}
		</Flex>
	);
};
